<template>
  <div class="account-manage">
    <!-- 按钮容器 -->
    <div class="button-container">
      <el-button type="primary" @click="handleAdd" class="add-btn">
        <i class="el-icon-plus"></i>
        新增账户
      </el-button>
    </div>
    
    <!-- 表格容器 -->
    <div class="table-container">
      <el-table
        :data="tableData"
        v-loading="loading"
        border
        stripe
        style="width: 100%"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column prop="username" label="用户名" min-width="200" align="center" />
        <el-table-column prop="role" label="类型" width="600" align="center">
          <template slot-scope="scope">
            <el-tag :type="getRoleTagType(scope.row.description
)" size="small">
              {{ getRoleLabel(scope.row.description
) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="400" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button 
              type="text" 
              @click="handleEdit(scope.row)"
              class="edit-btn"
            >
              编辑
            </el-button>
            <el-button 
              type="text" 
              @click="handleDelete(scope.row)"
              class="delete-btn"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.page + 1"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        />
      </div>
    </div>
    
    <!-- 账户对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      :close-on-click-modal="false"
      @close="resetForm"
      @opened="handleDialogOpened"
    >
      <el-form
        :model="form"
        :rules="formRules"
        ref="form"
        label-width="100px"
      >
        <el-form-item label="账号" prop="username">
          <el-input
            v-model="form.username"
            :placeholder="isEdit ? '' : '请输入账号'"
            :disabled="isEdit"
            clearable
          />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            :placeholder="isEdit ? '当前密码，点击眼睛图标查看' : '请输入密码'"
            clearable
          >
            <i 
              slot="suffix" 
              :class="showPassword ? 'el-icon-view' : 'el-icon-view'" 
              @click="togglePassword"
              style="cursor: pointer; color: #c0c4cc;"
            ></i>
          </el-input>
        </el-form-item>
        <el-form-item label="类型" prop="description">
          <el-select
            v-if="!isEdit"
            v-model="form.description"
            placeholder="请选择类型"
            style="width: 100%"
          >
            <el-option
              label="系统管理员"
              value="admin"
            />
            <el-option
              label="只读子账号"
              value="subadmin"
            />
          </el-select>
          <el-input
            v-else
            v-model="form.description"
            disabled
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'AccountManage',
  data() {
    return {
      loading: false,
      tableData: [],
      pagination: {
        page: 0,
        size: 20,
        total: 0
      },
      dialogVisible: false,
      submitLoading: false,
      isEdit: false,
      showPassword: false, // 控制密码显示状态
      form: {
        id: '',
        username: '',
        password: '',
        description: ''
      },
      formRules: {
        username: [
          { required: true, message: '请输入账号', trigger: 'blur' },
          { min: 3, max: 20, message: '账号长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
        ],
        description: [
          { required: true, message: '请选择类型', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑账户' : '新增账户'
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        // 真实API调用
        const response = await this.$http.get('/api/accounts/page', {
          params: {
            page: this.pagination.page,
            size: this.pagination.size
          }
        })
        
        // 过滤数据，只显示 isActive 为 true 的数据
        const allData = response.data.data || response.data
        this.tableData = allData.filter(item => item.isActive === true)
        this.pagination.total = response.data.total || 0
      } catch (error) {
        this.$message.error('获取账号列表失败')
        console.error('API Error:', error)
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    
    getRoleTagType(role) {
      const typeMap = {
        'super': 'danger',
        'admin': 'primary',
        'subadmin': 'success'
      }
      return typeMap[role] || 'info'
    },
    
    getRoleLabel(role) {
      const labelMap = {
        'super': '超级管理员',
        'admin': '管理员',
        'subadmin': '子管理员'
      }
      return labelMap[role] || '未知'
    },
    
    handleAdd() {
      this.isEdit = false
      this.resetForm()
      this.dialogVisible = true
    },
    
    handleEdit(row) {
      this.isEdit = true
      this.form = {
        id: row.id,
        username: row.username,
        password: row.password || '', // 使用真实密码，但初始状态是隐藏的
        description: row.description
      }
      this.dialogVisible = true
    },
    
    handleDialogOpened() {
      // 弹框打开时，重置密码显示状态为隐藏
      this.showPassword = false
    },
    
    togglePassword() {
      this.showPassword = !this.showPassword
    },
    
    async submitForm() {
      // 动态设置验证规则
      const rules = { ...this.formRules }
      if (this.isEdit) {
        // 编辑模式下不需要验证账号和类型
        rules.username = []
        rules.description = []
      }
      
      this.$refs.form.validate(async (valid) => {
        if (!valid) return
        
        this.submitLoading = true
        try {
          let response
          if (this.isEdit) {
            // 编辑
            response = await this.$http.put(`/api/accounts/${this.form.id}`, {
              username: this.form.username,
              password: this.form.password,
              description: this.form.description
            })
          } else {
            // 新增
            response = await this.$http.post('/api/accounts', {
              username: this.form.username,
              password: this.form.password,
              description: this.form.description
            })
          }
          
          // 使用接口返回的message
          this.$message.success(response.data.message || (this.isEdit ? '编辑账户成功' : '新增账户成功'))
          
          this.dialogVisible = false
          this.resetForm()
          this.fetchData() // 刷新列表
        } catch (error) {
          // 使用接口返回的错误message
          const errorMessage = error.response?.data?.message || (this.isEdit ? '编辑账户失败' : '新增账户失败')
          this.$message.error(errorMessage)
          console.error('Account Error:', error)
        } finally {
          this.submitLoading = false
        }
      })
    },
    
    resetForm() {
      this.form = {
        id: '',
        username: '',
        password: '',
        description: ''
      }
      this.showPassword = false // 重置密码显示状态
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    
    
    handleDelete(row) {
      this.$confirm(`确定要删除账号 ${row.username} 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await this.$http.put(`/api/accounts/${row.id}`, {
            username: row.username,
            password: row.password,
            description: row.description,
            isActive: false
          })
          
          if (response.data.success === true) {
            this.$message.success('删除成功')
            this.fetchData()
          } else {
            this.$message.error('删除失败')
          }
        } catch (error) {
          this.$message.error('删除失败')
          console.error('Delete Account Error:', error)
        }
      }).catch(() => {
        this.$message.info('已取消操作')
      })
    },
    
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.page = 0
      this.fetchData()
    },
    
    handleCurrentChange(val) {
      this.pagination.page = val - 1
      this.fetchData()
    }
  }
}
</script>

<style lang="scss" scoped>
.account-manage {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 120px);
}

.button-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
  
  .add-btn {
    background: #409EFF;
    border-color: #409EFF;
    border-radius: 4px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 500;
    
    &:hover {
      background: #66b1ff;
      border-color: #66b1ff;
    }
    
    i {
      margin-right: 6px;
    }
  }
}

.table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
  
  .pagination-container {
    margin-top: 20px;
    text-align: right;
    padding: 16px 0;
  }
}

.el-table {
  .edit-btn {
    color: #409EFF;
    font-size: 12px;
    padding: 0 4px;
    margin-right: 8px;
    white-space: nowrap;
    
    &:hover {
      color: #66b1ff;
    }
  }
  
  .delete-btn {
    color: #f56c6c;
    font-size: 12px;
    padding: 0 4px;
    white-space: nowrap;
    
    &:hover {
      color: #f78989;
    }
  }
}

// 表格样式优化
::v-deep .el-table {
  border-radius: 4px;
  overflow: hidden;
  
  .el-table__header {
    th {
      background: #f5f7fa;
      color: #606266;
      font-weight: 500;
      border-bottom: 1px solid #ebeef5;
    }
  }
  
  .el-table__body {
    tr {
      &:hover {
        background: #f5f7fa;
      }
    }
    
    td {
      border-bottom: 1px solid #ebeef5;
      padding: 12px 0;
    }
  }
}

// 分页样式优化
::v-deep .el-pagination {
  .el-pagination__total {
    color: #606266;
    font-weight: 400;
  }
  
  .el-pager li {
    border-radius: 4px;
    margin: 0 2px;
    
    &:hover {
      color: #409EFF;
    }
    
    &.active {
      background: #409EFF;
      color: #fff;
    }
  }
  
  .btn-prev,
  .btn-next {
    border-radius: 4px;
    margin: 0 2px;
    
    &:hover {
      color: #409EFF;
    }
  }
}
</style>
